import React, { useState, useRef, useEffect } from "react";
import {
  FaUser,
  FaMobileAlt,
  FaSearch,
  FaWhatsapp,
  FaPhone,
  FaCheck,
  FaTimes,
  FaComments,
  FaEnvelope,
} from "react-icons/fa";
import Select from "react-select";
import "./ChatWidget.css";

function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    selectedService: "",
  });
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);

  // Close widget when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !event.target.classList.contains("chat-float-btn")
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Fetch services when widget is opened
  useEffect(() => {
    if (open && services.length === 0) {
      setServicesLoading(true);
      setServicesError("");
      fetch(
        "https://n8n.srv841662.hstgr.cloud/webhook/drips_without_authentication"
      )
        .then((res) => res.json())
        .then((data) => {
          if (data && Array.isArray(data.data)) {
            setServices(data.data);
          } else {
            setServicesError("No services found");
          }
        })
        .catch(() => setServicesError("Failed to load services"))
        .finally(() => setServicesLoading(false));
    }
  }, [open, services.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (selectedOption) => {
    setForm((prev) => ({
      ...prev,
      selectedService: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Find the selected service object to get drip_id
    const selectedServiceObj = services.find(
      (s) => s.name === form.selectedService
    );
    const drip_id = selectedServiceObj ? selectedServiceObj.id : "";
    // Prepare payload for API
    const formData = {
      name: form.name,
      phone: form.phone,
      email: form.email,
      status: 1,
      drip_id,
      note: "",
      custom_fields: {},
    };
    try {
      await fetch("https://n8n.srv841662.hstgr.cloud/webhook/leads_template", {
        method: "POST",
        body: JSON.stringify(formData),
        headers: {
          "Content-Type": "application/json",
        },
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setForm({ name: "", email: "", phone: "", selectedService: "" });
        setOpen(false);
      }, 3000);
    } catch (error) {
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setForm({ name: "", email: "", phone: "", selectedService: "" });
        setOpen(false);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        className={`chat-float-btn${open ? " open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat widget"
      >
        <span className="chat-float-btn-icon">
          {open ? <FaTimes /> : <FaComments />}
        </span>
      </button>

      {open && (
        <div className="chat-widget-modal modern" ref={modalRef}>
          {!submitted ? (
            <form
              className="chat-form modern"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <h3 className="chat-title">Let's Get Connected</h3>

              <div className="progress-bar">
                <div className="progress-fill" style={{ width: "100%" }}></div>
              </div>

              <div className="input-fields-group">
                <label className="chat-label">
                  <div className="label-text">
                    <span className="icon">
                      <FaUser />
                    </span>
                    <span>Your Name</span>
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your name"
                    autoComplete="off"
                    className="chat-input"
                  />
                </label>

                <label className="chat-label">
                  <div className="label-text">
                    <span className="icon">
                      <FaMobileAlt />
                    </span>
                    <span>Phone Number</span>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your phone number"
                    autoComplete="off"
                    className="chat-input"
                  />
                </label>

                <label className="chat-label">
                  <div className="label-text">
                    <span className="icon">
                      <FaEnvelope />
                    </span>
                    <span>Email</span>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                    autoComplete="off"
                    className="chat-input"
                  />
                </label>

                <label className="chat-label">
                  <div className="label-text">
                    <span className="icon">
                      <FaSearch />
                    </span>
                    <span>What are you looking for?</span>
                  </div>
                  {servicesLoading ? (
                    <div className="services-loading">Loading services...</div>
                  ) : servicesError ? (
                    <div className="services-error">{servicesError}</div>
                  ) : (
                    <Select
                      name="selectedService"
                      value={
                        services.find((s) => s.name === form.selectedService)
                          ? {
                              value: form.selectedService,
                              label: form.selectedService,
                            }
                          : null
                      }
                      onChange={handleServiceChange}
                      options={services.map((service) => ({
                        value: service.name,
                        label: service.name,
                      }))}
                      placeholder="Select a service"
                      isClearable
                      classNamePrefix="react-select"
                      styles={{
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                          maxHeight: 200,
                          overflowY: "auto",
                          pointerEvents: "auto",
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          maxHeight: 200,
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                        }),
                      }}
                      menuPortalTarget={null}
                    />
                  )}
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="submit-btn modern"
                  disabled={
                    !form.name ||
                    !form.phone ||
                    !form.email ||
                    !form.selectedService ||
                    loading
                  }
                >
                  {loading ? <span className="spinner"></span> : "Send Inquiry"}
                </button>
              </div>

              <div className="contact-options">
                <p className="contact-text">Or reach us directly:</p>
                <div className="contact-buttons">
                  <a
                    href="https://wa.me/+919902699112?text=Hi%20Geosentry%2C%20I%20would%20like%20to%20know%20more%20about%20your%20fleet%20management%20solutions." // WhatsApp with default message
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-btn whatsapp-btn"
                  >
                    <span className="contact-icon">
                      <FaWhatsapp />
                    </span>
                    WhatsApp
                  </a>

                  <a
                    href="tel:+919902699112" // Replace with your phone number
                    className="contact-btn call-btn"
                  >
                    <span className="contact-icon">
                      <FaPhone />
                    </span>
                    Call Us
                  </a>
                </div>
              </div>
            </form>
          ) : (
            <div className="success-message">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h3>Thank You!</h3>
              <p>
                Your inquiry has been submitted successfully. Our team will
                contact you shortly.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ChatWidget;
